import Phaser from 'phaser';
import { loadAnimationManifest, loadGameData, loadPortraitManifest } from '../core/Loader';
import { DebugLog } from '../core/DebugLog';
import { loadPerformanceConfig } from '../core/PerformanceSettings';
import { SaveStore } from '../core/SaveStore';
import { DataRegistry } from '../systems/DataRegistry';
import { QuestSystem } from '../systems/QuestSystem';
import { AssetAnimationManifestEntry } from '../types/data';

function registerNpcSheetAnimations(scene: Phaser.Scene, item: AssetAnimationManifestEntry) {
  const base = item.baseKey ?? item.key.replace(/_sheet$/, '');
  const textureKey = item.key;
  const defs = [
    { key: `${base}-idle-down`, frames: [{ key: textureKey, frame: 0 }], frameRate: 1, repeat: -1 },
    { key: `${base}-idle-up`, frames: [{ key: textureKey, frame: 3 }], frameRate: 1, repeat: -1 },
    { key: `${base}-idle-left`, frames: [{ key: textureKey, frame: 6 }], frameRate: 1, repeat: -1 },
    { key: `${base}-idle-right`, frames: [{ key: textureKey, frame: 9 }], frameRate: 1, repeat: -1 },
    { key: `${base}-walk-down`, frames: scene.anims.generateFrameNumbers(textureKey, { start: 0, end: 2 }), frameRate: 6, repeat: -1 },
    { key: `${base}-walk-up`, frames: scene.anims.generateFrameNumbers(textureKey, { start: 3, end: 5 }), frameRate: 6, repeat: -1 },
    { key: `${base}-walk-left`, frames: scene.anims.generateFrameNumbers(textureKey, { start: 6, end: 8 }), frameRate: 6, repeat: -1 },
    { key: `${base}-walk-right`, frames: scene.anims.generateFrameNumbers(textureKey, { start: 9, end: 11 }), frameRate: 6, repeat: -1 },
  ];
  defs.forEach((def) => {
    if (!scene.anims.exists(def.key)) scene.anims.create(def as Phaser.Types.Animations.Animation);
  });
}

export class BootScene extends Phaser.Scene {
  constructor() {
    super('BootScene');
  }

  async create() {
    DebugLog.info('BootScene', 'create start');
    const [bundle, portraitManifest, animationManifest, performance] = await Promise.all([
      loadGameData(),
      loadPortraitManifest(),
      loadAnimationManifest(),
      loadPerformanceConfig(),
    ]);

    const registry = new DataRegistry(bundle);
    QuestSystem.initialize(registry.getAllQuests());
    this.registry.set('dataRegistry', registry);
    this.registry.set('portraitManifest', portraitManifest);
    this.registry.set('animationManifest', animationManifest);
    this.registry.set('performanceConfig', performance);

    const saved = SaveStore.loadSession();
    DebugLog.info('BootScene', 'session restored', saved);
    const targetVillageId = saved?.sceneType === 'house'
      ? bundle.houses.find((house) => house.id === saved.houseId)?.villageId ?? 'village01'
      : saved?.villageId ?? 'village01';
    const initialVillage = bundle.villages.find((village) => village.id === targetVillageId) ?? bundle.villages[0];
    const initialHouse = saved?.sceneType === 'house' ? bundle.houses.find((house) => house.id === saved.houseId) : undefined;

    if (performance.bootPreload.initialVillageOnly && initialVillage) this.load.image(initialVillage.bgKey, initialVillage.bgPath);
    else bundle.villages.forEach((village) => this.load.image(village.bgKey, village.bgPath));

    if (performance.bootPreload.savedHouseOnly && initialHouse) this.load.image(initialHouse.bgKey, initialHouse.bgPath);
    else bundle.houses.slice(0, 3).forEach((house) => this.load.image(house.bgKey, house.bgPath));

    if (performance.bootPreload.commonTextures) {
      this.load.spritesheet('player_sheet', '/assets/characters/player_sheet.png', { frameWidth: 60, frameHeight: 80 });
      this.load.image('npc_house', '/assets/characters/npc_house.png');
      this.load.image('npc_village', '/assets/characters/npc_village.png');
      this.load.image('tree_base', '/assets/objects/tree_base.png');
      this.load.image('tree_front', '/assets/objects/tree_front.png');
    }

    portraitManifest.forEach((item) => {
      if (!this.textures.exists(item.key)) this.load.image(item.key, item.path);
    });
    animationManifest.forEach((item) => {
      if (!this.textures.exists(item.key)) this.load.spritesheet(item.key, item.path, { frameWidth: item.frameWidth, frameHeight: item.frameHeight });
    });

    this.load.once(Phaser.Loader.Events.COMPLETE, () => {
      DebugLog.info('BootScene', 'boot preload complete');
      animationManifest.forEach((item) => {
        if (item.type === 'npc' && this.textures.exists(item.key)) registerNpcSheetAnimations(this, item);
      });
      this.scene.launch('UIScene');
      const uiScene = this.scene.get('UIScene');
      uiScene.events.emit('set-source', registry.sourceInfo);

      if (saved?.sceneType === 'house' && saved.houseId && registry.housesById.has(saved.houseId)) {
        DebugLog.info('BootScene', 'start HouseScene', { houseId: saved.houseId });
        this.scene.start('HouseScene', { houseId: saved.houseId });
        return;
      }
      const villageId = saved?.villageId && registry.villagesById.has(saved.villageId) ? saved.villageId : 'village01';
      DebugLog.info('BootScene', 'start VillageScene', { villageId, spawnX: saved?.spawnX, spawnY: saved?.spawnY });
      this.scene.start('VillageScene', { villageId, spawnX: saved?.spawnX, spawnY: saved?.spawnY });
    });
    this.load.start();
  }
}
